import {
	FormDrawer,
	FormDrawerContent,
	IconButton,
	TextareaField,
	Trigger,
	isSuccess,
	useBulkEdit,
	usePaginatedList,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { mapToBulkRequest } from "../../../../../shared/api/mappings";
import { eventUserRequestService } from "../../../shared/api/eventUserRequestService";
import { DeclineFormValues } from "../../../shared/types/eventUserRequest";

const emptyDeclineFormValues: DeclineFormValues = { declinationReason: "" };

export const EventUserRequestBulkEdit: FC = () => {
	const { t } = useTranslation();
	const { selectedRows, availableItemIds } = useBulkEdit();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const { searchParams } = usePaginatedList();

	const bulkApprove = useCallback(async () => {
		const result = await eventUserRequestService.bulkApproveRequests(
			mapToBulkRequest(selectedRows, searchParams?.search),
		);
		if (isSuccess(result)) {
			await queryClient.invalidateQueries({ queryKey: eventUserRequestService.openRequestsKey });
			showToast({
				type: "success",
				title: t("Approve"),
				body: t("Event requests are successfully approved"),
			});
		}
	}, [queryClient, searchParams?.search, selectedRows, showToast, t]);

	const bulkDecline = useCallback(
		async (formValues: DeclineFormValues) => {
			const result = await eventUserRequestService.bulkDeclineRequests({
				...mapToBulkRequest(selectedRows, searchParams?.search),
				declinationReason: formValues.declinationReason,
			});
			if (isSuccess(result)) {
				await queryClient.invalidateQueries({ queryKey: eventUserRequestService.openRequestsKey });
				showToast({
					type: "success",
					title: t("Decline"),
					body: t("Event requests are successfully declined"),
				});
				return true;
			}
			return false;
		},
		[queryClient, searchParams?.search, selectedRows, showToast, t],
	);

	const validate = useCallback(
		(values: DeclineFormValues) => {
			const errors: { [K in keyof DeclineFormValues]?: any } = {};

			if (!values.declinationReason) {
				errors.declinationReason = t("Field is required");
			}

			return errors;
		},
		[t],
	);

	return (
		<>
			<IconButton
				type="button"
				title={t("Approve")}
				size="small"
				disabled={selectedRows.length === 0 || availableItemIds.length === 0}
				iconSrc="/person_check.svg"
				onClick={bulkApprove}
			/>
			<Trigger
				render={(props) => (
					<FormDrawer
						{...props}
						onClose={props.handleClose}
						onSubmit={bulkDecline}
						renderForm={(props) => (
							<FormDrawerContent {...props}>
								<TextareaField name="declinationReason" label={t("Declination reason")} required />
							</FormDrawerContent>
						)}
						initialValues={emptyDeclineFormValues}
						title={t("Decline")}
						validate={validate}
					/>
				)}
			>
				{(props) => (
					<IconButton
						{...props}
						type="button"
						title={t("Decline")}
						size="small"
						disabled={selectedRows.length === 0 || availableItemIds.length === 0}
						iconSrc="/person_cancel.svg"
					/>
				)}
			</Trigger>
		</>
	);
};
